import { createSelector } from "@reduxjs/toolkit";
import { memoize } from "es-toolkit";

import {
  assert,
  getDesignerNodeDataByIdSelector,
  monolingualizeConfig,
  nodeDesignsByIdSelector,
  nodeIdParamSelector,
  processNodeSubtree,
  type DesignerNodeData,
  type GetDesignerNodeDataById,
  type MultilingualNodeConfig,
  type NodeConfig,
} from "@skilitics/nautilus-core-designer-data";

import type { DesignerNode, GetManifestByTypeId } from "../../types/index.ts";
import { fallbackLanguageCodesSelector } from "./activeLanguageSelectors.ts";
import { getManifestSelector } from "./appConfigSelectors.ts";

const monolingualizeConfigOptionsSelector = createSelector(
  fallbackLanguageCodesSelector,
  (appliedLanguageCodes): Parameters<typeof monolingualizeConfig>[1] => ({
    appliedLanguageCodes,
  }),
);

export type MonolingualizeConfig = (config: MultilingualNodeConfig) => NodeConfig;

export const monolingualizeConfigSelector = createSelector(
  monolingualizeConfigOptionsSelector,
  (options): MonolingualizeConfig => memoize((config: MultilingualNodeConfig) => monolingualizeConfig(config, options)),
);

export type GetMonolingualDesignerNodeData = (id: number) => DesignerNodeData; // technically should type map .config to NodeConfig but need to do children too

export const getMonolingualDesignerNodeDataSelector = createSelector(
  [getDesignerNodeDataByIdSelector, monolingualizeConfigSelector],
  (getDesignerNodeData, monolingualize): GetMonolingualDesignerNodeData =>
    memoize((id: number) =>
      processNodeSubtree(getDesignerNodeData(id), (node) => ({ ...node, config: monolingualize(node.config) })),
    ),
);

export const designerNodeConfigOrNullSelector = createSelector(
  [nodeDesignsByIdSelector, monolingualizeConfigSelector, nodeIdParamSelector],
  (designsById, monolingualize, nodeId) => {
    if (!(nodeId in designsById)) return null;
    return monolingualize(designsById[nodeId].config);
  },
);

export const designerNodeConfigSelector = createSelector(
  [designerNodeConfigOrNullSelector],
  (config) => {
    assert(config);
    return config;
  },
  { devModeChecks: { identityFunctionCheck: "never" } },
);

interface Options {
  getManifest: GetManifestByTypeId;
  getNode: GetDesignerNodeDataById;
}

/** For tests do not use directly */
export const validateConfigCreate =
  ({ getManifest, getNode }: Options) =>
  (idOrNode: DesignerNode | number, config: NodeConfig) => {
    const node = typeof idOrNode === "number" ? getNode(idOrNode) : idOrNode;
    const {
      designer: { configSchemas },
    } = getManifest(node.typeId);
    if (!configSchemas) return;
    if (!node.version) throw Error("No node version");
    const schema = configSchemas[node.version] ?? configSchemas.current;
    if (!schema) return; // TODO: semver order to choose most recent version?
    schema.parse(config); // could use features to transform, defaults etc (and return) but may not be wise
  };

export const validateNodeConfigSelector = createSelector(
  [getManifestSelector, getDesignerNodeDataByIdSelector],
  (getManifest, getNode) => validateConfigCreate({ getManifest, getNode }),
);

export type ValidateNodeConfig = ReturnType<typeof validateConfigCreate>;
